import React, { useState } from 'react';
import { Mail, Lock, LogIn, AlertCircle } from 'lucide-react';
import { loginUser } from './api/auth';
import { sessionManager } from './api/sessionManager';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!password) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    try {
      const result = await loginUser(email, password);

      if (result.success) {
        console.log('✅ Login API Response:', JSON.stringify(result, null, 2));
        
        // Debug: Check what fields are in the response
        console.log('Response structure:', {
          hasToken: !!result.data?.token,
          hasUser: !!result.data?.user,
          allKeys: Object.keys(result.data || {}),
          fullData: result.data
        });
        
        // Validate token exists before storing
        if (!result.data?.token) {
          console.error('❌ No token received from API');
          console.error('API Response was:', result.data);
          
          // Try to provide helpful message based on what we got
          const responseKeys = Object.keys(result.data || {});
          const message = responseKeys.length === 0 
            ? 'Login failed: Server returned empty data' 
            : `Login failed: No token received from server. Got: ${responseKeys.join(', ')}`;
          
          setError(message);
          setLoading(false);
          return;
        }
        
        console.log('🔑 Token found, length:', result.data.token.length);
        
        // Store token and user info using SessionManager
        const tokenStored = sessionManager.setToken(result.data.token);
        const userStored = sessionManager.setUser(result.data.user || {});
        
        if (!tokenStored) {
          console.error('❌ Failed to store token in sessionManager');
          setError('Failed to save session. Please try again.');
          setLoading(false);
          return;
        }
        
        console.log('✅ Session data stored successfully');
        
        // Verify token is actually in storage before proceeding
        const storedToken = sessionManager.getToken();
        if (!storedToken) {
          console.error('❌ Token verification failed after storage');
          setError('Session verification failed. Please try again.');
          setLoading(false);
          return;
        }
        
        console.log('✅ Token verified in storage, proceeding with login');
        
        setEmail('');
        setPassword('');
        
        // Small delay to ensure state updates are flushed
        setTimeout(() => {
          onLoginSuccess();
        }, 100);
      } else {
        console.error('❌ Login API returned success: false');
        console.error('Error from API:', result.error);
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      const errorMsg = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
      console.error('🔴 Login exception:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        {/* Left side - Branding */}
        <div className="login-branding">
          <div className="login-branding-content">
            <div className="login-logo">
              <div className="logo-circle"><img src="/assets/gold_bars.png" alt="Banking CRM Logo" /></div>
              <h1>Banking CRM</h1>
            </div>
            <p className="login-tagline">Grow Your Financial Business</p>
            <div className="login-features">
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <div className="feature-text">
                  <span className="feature-title">Real-time Analytics</span>
                  <span className="feature-desc">Track all metrics instantly</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👥</div>
                <div className="feature-text">
                  <span className="feature-title">Customer Management</span>
                  <span className="feature-desc">Manage clients effectively</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">💰</div>
                <div className="feature-text">
                  <span className="feature-title">Transaction Tracking</span>
                  <span className="feature-desc">Monitor all payments</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="login-form-container">
          <div className="login-form-content">
            <h2>Welcome Back</h2>
            <p className="login-subtitle">Sign in to your account to continue</p>

            {error && (
              <div className="error-alert">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="form-options">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <a href="#forgot" className="forgot-password">
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-button"
                disabled={loading}
              >
                <LogIn size={20} />
                <span>{loading ? 'Signing in...' : 'Sign In'}</span>
              </button>
            </form>

            {/* Footer */}
            <div className="login-footer">
              <p>Don't have an account? <a href="#signup">Create one</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
